import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import {
  ArrowRight,
  Mail,
  Phone,
  Send,
  Loader2,
  CheckCircle2,
  MessageSquare,
} from "lucide-react";
import { submitInquiry } from "@/lib/inquiry.functions";
import { localBusinessJsonLd } from "@/lib/seo.schemas";

export const Route = createFileRoute("/kontakt")({
  head: () => ({
    meta: [
      { title: "Kontakta Event Sverige" },
      {
        name: "description",
        content:
          "Berätta vad ni planerar och hur vi når er. Vi hjälper er att välja ett upplägg och stämmer av datum, lokal och omfattning före offert.",
      },
      { property: "og:title", content: "Kontakta Event Sverige" },
      {
        property: "og:description",
        content:
          "Berätta vad ni planerar och hur vi når er. Vi hjälper er att välja ett upplägg och stämmer av datum, lokal och omfattning före offert.",
      },
      { property: "og:url", content: "/kontakt" },
    ],
    links: [{ rel: "canonical", href: "https://eventsverige.se/kontakt" }],
    scripts: [{
      type: "application/ld+json",
      children: JSON.stringify(localBusinessJsonLd()),
    }],
  }),
  component: KontaktPage,
});

function KontaktPage() {
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });

  const update = (field: keyof typeof form, value: string) => {
    setForm((f) => ({ ...f, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Fyll i namn och e-post");
      return;
    }
    setLoading(true);
    try {
      await submitInquiry({
        data: {
          name: form.name,
          email: form.email,
          phone: form.phone,
          company: "",
          eventType: "",
          eventDate: "",
          location: "",
          guests: 0,
          message: form.message,
          products: [],
        },
      });
      setSent(true);
      toast.success("Meddelande skickat! Vi återkommer snart.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Något gick fel");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden bg-primary text-primary-foreground">
        <div className="absolute inset-0 bg-grain opacity-20" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 lg:py-22">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 rounded-full bg-accent/15 text-accent px-4 py-1.5 text-xs font-bold uppercase tracking-widest mb-6">
              <MessageSquare className="h-3.5 w-3.5" />
              Kontakt
            </div>
            <h1 className="font-display text-5xl sm:text-6xl font-extrabold leading-[0.95]">
              Vi hjälper dig
              <br />
              <span className="text-accent">gärna.</span>
            </h1>
            <p className="mt-6 text-lg text-primary-foreground/80 max-w-xl">
              Berätta vad ni planerar och hur vi når er. Vi hjälper er att välja ett upplägg och
              stämmer av datum, lokal och omfattning före offert.
            </p>
          </div>
        </div>
      </section>

      {/* CONTACT CARDS + FORM */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid gap-12 lg:grid-cols-2">
          {/* Contact info */}
          <div>
            <span className="text-xs font-bold uppercase tracking-widest text-accent">Kontaktuppgifter</span>
            <h2 className="mt-2 font-display text-3xl sm:text-4xl font-extrabold">
              Så når du oss
            </h2>
            <p className="mt-3 text-muted-foreground">
              Vi finns här när du behöver oss — innan, under och efter ditt event.
            </p>
            <div className="mt-8 space-y-5">
              {[
                { icon: Mail, label: "E-post", value: "info@eventsverige.se", href: "mailto:info@eventsverige.se" },
                { icon: Phone, label: "Telefon", value: "031-797 55 99", href: "tel:+46317975599" },
              ].map((item) => {
                const Icon = item.icon;
                const content = (
                  <div className="flex items-start gap-4 p-4 rounded-2xl bg-card border border-border hover:shadow-md transition-all">
                    <div className="h-10 w-10 rounded-xl bg-accent/15 grid place-items-center shrink-0">
                      <Icon className="h-5 w-5 text-accent-foreground" />
                    </div>
                    <div>
                      <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{item.label}</div>
                      <div className="text-sm font-medium mt-0.5">{item.value}</div>
                    </div>
                  </div>
                );
                return item.href ? (
                  <a key={item.label} href={item.href} className="block">
                    {content}
                  </a>
                ) : (
                  <div key={item.label}>{content}</div>
                );
              })}
            </div>

            {/* Quick link to inquiry */}
            <div className="mt-8 rounded-2xl bg-primary text-primary-foreground p-6">
              <h3 className="font-display text-lg font-bold">Vill du begära offert direkt?</h3>
              <p className="mt-1 text-sm text-primary-foreground/80">
                Bygg din önskelista med produkter och skicka en förfrågan — det tar 2 minuter.
              </p>
              <Link
                to="/forfragan"
                className="mt-4 inline-flex items-center gap-2 rounded-full bg-accent text-accent-foreground px-5 py-2.5 text-sm font-bold hover:bg-accent/90 transition"
              >
                Gå till förfrågan <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>

          {/* Form */}
          <div>
            {sent ? (
              <div className="rounded-2xl bg-card border border-border p-10 text-center">
                <div className="mx-auto h-16 w-16 rounded-full bg-green-100 grid place-items-center mb-6">
                  <CheckCircle2 className="h-8 w-8 text-green-600" />
                </div>
                <h3 className="font-display text-2xl font-bold">Tack för ditt meddelande!</h3>
                <p className="mt-3 text-muted-foreground max-w-md mx-auto">
                  Vi har tagit emot ditt meddelande. Vårt team granskar det och återkommer.
                </p>
                <div className="mt-8 flex flex-wrap gap-3 justify-center">
                  <Link
                    to="/produkter"
                    className="inline-flex items-center gap-2 rounded-full bg-accent text-accent-foreground px-6 py-3 font-bold hover:bg-accent/90 transition"
                  >
                    Bläddra i sortimentet <ArrowRight className="h-4 w-4" />
                  </Link>
                  <Link
                    to="/"
                    className="inline-flex items-center gap-2 rounded-full bg-secondary px-6 py-3 font-semibold hover:bg-secondary/70 transition"
                  >
                    Till startsidan
                  </Link>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="rounded-2xl bg-card border border-border p-6 sm:p-8 space-y-5">
                <div>
                  <h3 className="font-display text-xl font-bold">Skicka ett meddelande</h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Fyll i formuläret så hör vi av oss så snart vi kan.
                  </p>
                </div>

                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold mb-1.5">
                      Namn <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="name"
                      type="text"
                      required
                      value={form.name}
                      onChange={(e) => update("name", e.target.value)}
                      placeholder="Ditt namn"
                      className="w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold mb-1.5">
                      E-post <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="email"
                      type="email"
                      required
                      value={form.email}
                      onChange={(e) => update("email", e.target.value)}
                      placeholder="namn@exempel.se"
                      className="w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold mb-1.5">
                    Telefon
                  </label>
                  <input
                    id="phone"
                    type="tel"
                    value={form.phone}
                    onChange={(e) => update("phone", e.target.value)}
                    placeholder="070-123 45 67"
                    className="w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-semibold mb-1.5">
                    Meddelande
                  </label>
                  <textarea
                    id="message"
                    rows={5}
                    value={form.message}
                    onChange={(e) => update("message", e.target.value)}
                    placeholder="Skriv ditt meddelande här..."
                    className="w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-y"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-accent text-accent-foreground px-6 py-3.5 text-base font-bold hover:bg-accent/90 transition disabled:opacity-60"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      Skickar...
                    </>
                  ) : (
                    <>
                      <Send className="h-5 w-5" />
                      Skicka meddelande
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
